import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ShoppingBag, Images } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { imgUrl, priceLabel } from "@/lib/api";

const PackCard = ({ p, i, onBuy }) => {
  const inner = (
    <div className={`flex h-full flex-col rounded-[3px] p-7 ${p.featured ? "bg-[#071D1F]" : "border border-[#163D43] bg-[#0B2326]/70"}`}>
      <div className="flex items-start justify-between">
        <span className="font-display text-6xl font-semibold leading-none text-teal-50 md:text-7xl">{p.version || "—"}</span>
        {p.tag && <span className="font-mono-x rounded-full border border-[#2DD4BF]/50 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#2DD4BF]">{p.tag}</span>}
      </div>
      <h3 className="font-display mt-6 text-xl font-semibold text-teal-50">{p.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-teal-200/90">{p.description}</p>
      {p.features?.length > 0 && (
        <ul className="mt-5 space-y-2">
          {p.features.map((f) => (
            <li key={f} className="flex gap-2 text-sm text-teal-100"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2DD4BF]" />{f}</li>
          ))}
        </ul>
      )}
      <div className="mt-auto pt-8">
        <span className="font-display text-lg text-teal-50" data-testid={`pack-price-${i}`}>{priceLabel(p, "Em breve")}</span>
        <div className="mt-4 flex items-center gap-2">
          <Link to={`/pack/${p.id}`} data-testid={`pack-view-${i}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-[#163D43] px-4 py-2.5 text-sm text-teal-100 transition-colors hover:border-[#2DD4BF]/60">
            <Images className="h-4 w-4" /> Prévias
          </Link>
          <button onClick={() => onBuy(p)} data-testid={`pack-buy-${i}`} className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${p.featured ? "bg-[#2DD4BF] text-[#041214]" : "border border-[#2DD4BF]/60 text-[#2DD4BF]"}`}>
            <ShoppingBag className="h-4 w-4" /> Comprar
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <Reveal delay={i * 0.08} className="h-full" data-testid={`pack-card-${i}`}>
      <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 260, damping: 20 }} className={`h-full ${p.featured ? "glow-border rounded-[4px] p-px shadow-[0_0_60px_-10px_rgba(45,212,191,0.45)]" : ""}`}>
        {inner}
      </motion.div>
    </Reveal>
  );
};

export const Packs = ({ packs, onBuy }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const cover = packs.find((p) => p.image_url)?.image_url || "/img/pack.jpeg";
  return (
    <section id="pack-gospel" ref={ref} className="grain relative overflow-hidden bg-[#071D1F] py-24 md:py-36" data-testid="packs-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow n="02">Infoprodutos para designers</Eyebrow>
            <Reveal>
              <h2 className="font-display mt-6 text-6xl font-bold uppercase leading-[0.85] text-teal-50 md:text-8xl">
                Pack<br /><span className="font-serif-i normal-case text-[#2DD4BF]">Gospel</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-teal-100/85 md:text-lg">Packs de artes editáveis feitos para outros designers acelerarem seus projetos para igrejas e ministérios. Quatro versões — escolha a sua.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden rounded-[3px] [clip-path:polygon(8%_0,100%_0,100%_88%,92%_100%,0_100%,0_12%)]" data-testid="pack-cover-frame">
              <motion.img style={{ y: imgY }} src={imgUrl(cover)} alt="Pack Gospel" className="absolute inset-0 h-[124%] w-full -translate-y-[12%] object-cover" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_30%,rgba(4,18,20,0.75))]" />
            </div>
          </div>
        </div>
        <div className="mt-20 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {packs.map((p, i) => <PackCard key={p.id} p={p} i={i} onBuy={onBuy} />)}
        </div>
      </div>
    </section>
  );
};
